import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { X, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GHOST_IMAGE = "https://customer-assets.emergentagent.com/job_premium-channels-5/artifacts/tp0qgply_image.png";

export default function GhostChat() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm Ghost, your Prestigious TV assistant. How can I help you today? Feel free to ask about our streaming service, pricing, or device compatibility."
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const isAdminPage = location.pathname.startsWith("/admin");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ghost-chat`, {
        message: userMessage,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: response.data.response 
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I'm having trouble connecting right now. Please try again or contact us directly for assistance." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Don't render on admin pages
  if (isAdminPage) {
    return null;
  }

  return (
    <>
      {/* Chat Button with Label */}
      <div className="fixed bottom-8 right-8 z-50 flex items-center gap-3">
        {!isOpen && (
          <div className="hidden sm:block bg-gradient-to-r from-[#1E1B4B] to-[#121212] border border-[#D4AF37]/30 px-4 py-2 animate-fade-in mr-2">
            <p className="text-sm text-white/70">Need help choosing?</p>
            <p className="text-xs text-[#00D4FF]">Ask me</p>
          </div>
        )}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="ghost-btn overflow-hidden flex-shrink-0"
          data-testid="ghost-chat-btn"
          aria-label="Open chat"
        >
          {isOpen ? (
            <X className="w-6 h-6 text-[#D4AF37]" />
          ) : (
            <img src={GHOST_IMAGE} alt="Ghost" className="w-10 h-10 object-cover" />
          )}
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="ghost-window" style={{ bottom: '7rem', right: '2rem' }} data-testid="ghost-chat-window">
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center gap-3 bg-gradient-to-r from-[#1E1B4B]/50 to-transparent">
            <img src={GHOST_IMAGE} alt="Ghost" className="w-10 h-10 object-cover rounded" />
            <div>
              <h3 className="font-['Playfair_Display'] font-bold text-white">Ghost</h3>
              <p className="text-xs text-[#00D4FF]">TV Assistant</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[320px]">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] p-3 ${
                    msg.role === "user"
                      ? "bg-[#D4AF37] text-black"
                      : "bg-white/10 text-white/90"
                  }`}
                  data-testid={`chat-message-${idx}`}
                >
                  <p className="text-sm leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/10 p-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-white/10 flex gap-2">
            <Input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything..."
              className="flex-1 input-dark text-sm"
              disabled={loading}
              data-testid="ghost-chat-input"
            />
            <Button 
              type="submit" 
              disabled={loading || !input.trim()}
              className="bg-[#D4AF37] hover:bg-[#C5A059] text-black w-10 h-10 p-0"
              data-testid="ghost-chat-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
